import React from "react";
import { MapPin } from "lucide-react";
import { cn } from "../../lib/utils";

export default function HeroCard({
  title,
  titleClassName,
  subtitle,
  icon,
  iconContainerClassName,
  actions,
  stats,
  children,
  onLocationClick,
  gradient = "from-[#0b3b82] via-[#1b4f9c] to-[#2563eb]",
  variant = "compact",
  className,
}) {
  return (
    <div className={cn("px-3 pt-3 sm:px-4 bg-page-bg", className)}>
      <div
        className={cn(
          "relative overflow-hidden rounded-hero text-white",
          "bg-gradient-to-br",
          // Sombra plana sutil (no la elevada-coloreada shadow-blue, que era lenguaje de
          // botón CTA). El header se lee como banner, no como pulsable. dark:shadow-none
          // se conserva por el fix iOS (#15).
          "shadow-sm dark:shadow-none",
          gradient,
          variant === "compact"
            ? "px-4 py-3"
            : "px-5 py-4"
        )}
        style={{ WebkitMaskImage: "-webkit-radial-gradient(white, black)" }}
      >
        {/* Shimmer/gloss superior eliminado a propósito (UX: quitaba "afán de botón").
            El fix iOS de esquinas no depende de él — se sostiene con overflow-hidden +
            WebkitMaskImage + dark:shadow-none + blobs en rounded-[inherit] overflow-hidden. */}

        {/* AMBIENT — wrapped in overflow-hidden + rounded-[inherit] so iOS Safari clips the
            blur layers correctly within the parent's border-radius composite layer */}
        <div className="absolute inset-0 rounded-[inherit] overflow-hidden pointer-events-none">
          <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute -bottom-20 -left-20 h-56 w-56 rounded-full bg-white/[0.05] blur-2xl" />
        </div>

        {/* 🔥 HEADER */}
        <div className="relative z-10 flex items-start justify-between gap-4">

          {/* 🔥 LEFT SIDE */}
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">

            {/* 🔥 ICON */}
            {icon && (
              <div className={iconContainerClassName ?? "h-9 w-9 sm:h-10 sm:w-10 shrink-0 rounded-[11px] sm:rounded-[12px] bg-white/10 border border-white/20 backdrop-blur-md flex items-center justify-center overflow-hidden shadow-inner"}>
                {icon}
              </div>
            )}

            {/* 🔥 TEXT */}
            <div className="flex-1 min-w-0">

              <h2
                className={cn(
                  "text-[18px] sm:text-[22px] font-semibold tracking-tight leading-tight text-white",
                  titleClassName ?? "line-clamp-2 break-words"
                )}
              >
                {title}
              </h2>

              {subtitle && (
                  <p
                    className="
                      mt-0.5
                      text-[14px]
                      text-white/80
                      break-words
                    "
                  >
                    {subtitle}
                  </p>
              )}

              {/* 🔥 RICH CONTENT */}
              {variant === "rich" && (
                <div className="mt-3 space-y-2">
                  {children}
                </div>
              )}

            </div>

          </div>

          {/* 🔥 RIGHT SIDE */}
          <div className="flex flex-col items-end gap-2 shrink-0">

            {actions && (
              <div className="self-end">
                {actions}
              </div>
            )}

            {onLocationClick && (
              <button
                type="button"
                onClick={onLocationClick}
                aria-label="Ver ubicación"
                className="
                  h-9
                  w-9
                  rounded-full
                  border border-white/20
                  bg-white/10
                  backdrop-blur-xl
                  text-white
                  flex
                  items-center
                  justify-center
                  transition-all duration-200 ease-out
                  hover:bg-white/20 hover:scale-105
                  active:scale-[0.98]
                "
              >
                <MapPin size={15} />
              </button>
            )}

          </div>

        </div>

        {/* 🔥 STATS */}
        {variant === "compact" && stats && (
          <div className="relative z-10 mt-5 space-y-3">

            {/* 🔥 STATS GRID */}
            <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
              {stats}
            </div>

          </div>
        )}
      </div>
    </div>
  );
}